import hash from 'object-hash';

import { Timeline } from './models/Timeline';

import type { Renderer } from '../renderer/Renderer';
import type { Element } from './models';
import type { VideoEditor } from './VideoEditor';

/**
 * RendererSynchronizer class is responsible for managing the synchronization between
 * the Timeline and the Renderer in the video editing system
 */
export class RendererSynchronizer {
  private editor: VideoEditor;
  private renderer?: Renderer;
  private dirty: boolean = false;
  private lastRendererHash: string = '';
  private isSyncingFromRenderer: boolean = false;
  private isSyncingToRenderer: boolean = false;
  private lastSyncTime: number = 0;
  private pendingSync: boolean = false;
  private syncTimeoutId: NodeJS.Timeout | null = null;
  private throttleInterval: number = 250;

  constructor(editor: VideoEditor) {
    this.editor = editor;
  }

  connectRenderer(renderer: Renderer): void {
    this.renderer = renderer;

    // Store the original handler if it exists
    const originalOnStateChange = this.renderer.onStateChange;

    // Set up our handler that preserves the original functionality
    this.renderer.onStateChange = state => {
      // If we're not in the process of syncing TO the renderer, update FROM the renderer
      if (!this.isSyncingToRenderer) {
        this.syncFromRenderer(state);

        // Call the original handler after we've processed the state
        if (
          originalOnStateChange &&
          typeof originalOnStateChange === 'function'
        ) {
          try {
            originalOnStateChange(state);
          } catch (error) {
            console.error('Error in original onStateChange handler:', error);
          }
        }
      }
    };
  }

  markDirty(source: string): void {
    if (!this.dirty) {
      this.dirty = true;
    }
  }

  async syncToRenderer(): Promise<void> {
    if (!this.renderer || this.isSyncingFromRenderer) {
      return;
    }

    const timeline = this.editor.timeline;
    const json = timeline.toJSON();
    const newStateHash = hash(json);
    if (newStateHash === this.lastRendererHash) {
      return;
    }

    const now = Date.now();
    if (
      this.isSyncingToRenderer ||
      this.pendingSync ||
      now - this.lastSyncTime < this.throttleInterval
    ) {
      if (!this.pendingSync) {
        this.pendingSync = true;
        if (this.syncTimeoutId) clearTimeout(this.syncTimeoutId);
        this.syncTimeoutId = setTimeout(() => {
          this.pendingSync = false;
          this.syncToRenderer();
        }, this.throttleInterval);
      }
      return;
    }

    this.lastSyncTime = now;
    this.isSyncingToRenderer = true;
    this.lastRendererHash = newStateHash;

    // Preserve fonts from the current renderer source
    const currentSource = this.renderer.getSource();
    const currentFonts = currentSource?.fonts;
    const sourceWithFonts = currentFonts
      ? { ...json, fonts: currentFonts }
      : json;

    // Set source on renderer
    await this.renderer
      .setSource(sourceWithFonts, true)
      .catch(error => {
        console.error('Error syncing to renderer:', error);
      })
      .finally(() => {
        this.isSyncingToRenderer = false;
      });
  }

  syncFromRenderer(state: any): void {
    // First normalize the state to our format
    const normalizedState = this.normalizeRendererState(state);

    // Generate a hash of the state to check for uniqueness
    const stateHash = hash(normalizedState);

    // Special check: sometimes a "duplicate" state may actually be a confirmation
    // of a completed timeline operation (drag, shift, etc), so we need a more
    // detailed comparison than just the hash
    let isDuplicate = false;
    const previousHash = this.lastRendererHash;

    // Save the hash AFTER determining if it's a duplicate
    this.lastRendererHash = stateHash;

    if (stateHash === previousHash) {
      // Before ignoring, let's check if there are meaningful differences
      // such as exact element positions that might not affect the hash
      let shouldProcessAnyway = false;

      // Check if this is a timeline-specific event that we should process anyway
      try {
        if (
          state.source === 'timeline_event' ||
          state.activeTool === 'timeline' ||
          state.lastAction === 'timeline_drag' ||
          state.lastAction === 'element_move'
        ) {
          shouldProcessAnyway = true;
        }

        // Compare element positions exactly, hash might not catch slight differences
        if (
          !shouldProcessAnyway &&
          normalizedState.elements &&
          this.editor.timeline.allElements.length > 0
        ) {
          shouldProcessAnyway = normalizedState.elements.some((el: Element) => {
            const existing = this.editor.timeline.getElement(el.id);
            if (existing && Math.abs(existing.time - el.time) > 0.001) {
              return true; // Position changed
            }
            return false;
          });
        }
      } catch (e) {
        console.error('Error in duplicate detection:', e);
      }

      if (!shouldProcessAnyway) {
        isDuplicate = true;
      }
    }

    // If it's really a duplicate with no significant changes, skip processing
    if (isDuplicate) {
      return;
    }

    // Set flag to prevent recursion
    this.isSyncingFromRenderer = true;

    try {
      // Create a new timeline from the normalized state
      const newTimeline = Timeline.fromJSON(normalizedState);

      // Notify the VideoEditor of the state change with the new timeline
      if (this.editor) {
        this.editor.syncFromRenderer(normalizedState, newTimeline);
      }

      // Mark as not dirty since we just updated from renderer
      this.dirty = false;
    } catch (error) {
      console.error('Error updating timeline from renderer state:', error);
    } finally {
      // Always reset the flag when done
      this.isSyncingFromRenderer = false;
    }
  }

  /**
   * Normalizes the renderer state to match what our Timeline.fromJSON expects
   * Takes all elements.source data wholesale and ignores top-level element properties
   */
  private normalizeRendererState(rendererState: any): Record<string, any> {
    // Create a deep copy to avoid modifying the original
    const state = JSON.parse(JSON.stringify(rendererState));

    // Ensure we have an elements array
    if (!state.elements && state.source && state.source.elements) {
      state.elements = state.source.elements;
    }

    if (!state.elements) {
      state.elements = [];
    }

    // Process each element to use source properties exclusively
    state.elements = this.normalizeElements(state.elements);

    // Set required properties for Timeline.fromJSON
    if (!state.width) state.width = 1920;
    if (!state.height) state.height = 1080;
    if (!state.duration) state.duration = 10;

    // Ignore all other top-level metadata except essential properties
    const normalizedState: Record<string, any> = {
      width: state.width,
      height: state.height,
      duration: state.duration,
      elements: state.elements,
    };

    // Preserve format if available as it's needed for proper rendering
    if (state.format) {
      normalizedState.format = state.format;
    }

    return normalizedState;
  }

  /**
   * Normalizes an array of elements and handles nested elements in compositions
   * @param elements Array of elements to normalize
   * @returns Normalized elements array
   */
  private normalizeElements(elements: any[]): any[] {
    if (!Array.isArray(elements)) {
      return [];
    }

    return elements.map((element: any) => {
      // Create a new element object that will contain only essential data
      const normalizedElement: any = {};

      // Preserve the ID if it exists, or generate a new one
      normalizedElement.id =
        element.id ||
        `generated-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // If source exists and is an object, use it as the primary data source
      if (element.source && typeof element.source === 'object') {
        // Copy all properties from source object
        Object.keys(element.source).forEach(key => {
          normalizedElement[key] = element.source[key];
        });
      } else {
        // If the element doesn't have a source object, preserve critical properties
        // Copy essential properties
        const essentialProps = ['type', 'start', 'duration', 'trackId'];
        essentialProps.forEach(prop => {
          if (element[prop] !== undefined) {
            normalizedElement[prop] = element[prop];
          }
        });
      }

      // Handle timing property mapping for compatibility (only if not in source)
      if (
        normalizedElement.start === undefined &&
        element.globalTime !== undefined
      ) {
        normalizedElement.start = element.globalTime;
      }

      // Handle composition elements with nested elements
      if (
        (normalizedElement.type === 'composition' ||
          element.type === 'composition') &&
        element.elements &&
        Array.isArray(element.elements)
      ) {
        normalizedElement.elements = this.normalizeElements(element.elements);
      }

      return normalizedElement;
    });
  }
}
